/** V2 — Coordonnées, indice de faisabilité, demande de rappel, confirmation, fin qualif. */
import { useState } from "react";
import { FAISABILITE_UI, CRENEAUX, CONSENT_TEXT, PHONE_HINT } from "./simConstants";

export function LegalLinks({ testid }) {
  return (
    <p className="text-[11px] text-slate-400 text-center mt-3" data-testid={testid}>
      <a href="/mentions-legales" target="_blank" rel="noreferrer" className="underline hover:text-slate-600">Mentions légales</a>
      <span className="mx-2">·</span>
      <a href="/confidentialite" target="_blank" rel="noreferrer" className="underline hover:text-slate-600">Politique de confidentialité</a>
    </p>
  );
}
import { BigButton, GhostButton, StepTitle, TextField, CityInput, Chips, SubLabel, SimIcon } from "./SimPrimitives";
import { Check, Phone } from "lucide-react";
import { isDAv2 } from "@/lib/da";
import { SimShareBlock } from "./SimShare";

export function StepLanding({ config, onStart }) {
  return (
    <div className="pt-6 pb-4" data-testid="sim-landing">
      <div className="text-center">
        <div className="mb-4 flex justify-center"><SimIcon emoji="🏡" size="xl" /></div>
        <h1 className="font-extrabold text-slate-900 text-[28px] md:text-[36px] leading-tight tracking-tight">Votre projet immobilier est-il finançable ?</h1>
        <p className="text-slate-600 text-[15px] mt-3 leading-relaxed">Obtenez en <strong>2 à 3 minutes</strong> un premier avis sur la faisabilité de votre financement, puis échangez avec un courtier.</p>
      </div>
      <ul className="mt-8 space-y-3">
        {["Premier avis immédiat", "Gratuit et sans engagement", "Un courtier vous rappelle si vous le souhaitez"].map((t) => (
          <li key={t} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-100 shadow-sm">
            <span className="text-emerald-500 text-lg inline-flex"><Check size={18} strokeWidth={3} /></span><span className="text-slate-800 font-semibold text-[14px]">{t}</span>
          </li>
        ))}
      </ul>
      <div className="mt-8">
        <BigButton onClick={onStart} testid="sim-landing-cta">{config.cta_landing || "Vérifier la faisabilité de mon projet"}</BigButton>
        <p className="text-[11px] text-slate-400 text-center mt-3 leading-relaxed">{config.disclaimer}</p>
        <LegalLinks testid="sim-landing-legal-links" />
      </div>
    </div>
  );
}

export function StepComputing() {
  return (
    <div className="pt-12 pb-4 text-center" data-testid="sim-computing">
      <div className="mb-4 flex justify-center animate-pulse"><SimIcon emoji="✨" size="xl" /></div>
      <h1 className="font-extrabold text-slate-900 text-[24px] leading-tight">Nous analysons votre projet…</h1>
      <div className="mt-8 space-y-3 max-w-xs mx-auto">
        {["Projet", "Profil", "Revenus & charges", "Apport"].map((k, i) => (
          <div key={k} className="flex items-center justify-between px-4 py-2 rounded-xl bg-white border border-slate-100" style={{ animationDelay: `${i * 150}ms` }}>
            <span className="text-slate-700 font-bold text-[14px]">{k}</span><span className="text-emerald-500 font-extrabold inline-flex"><Check size={18} strokeWidth={3} /></span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function StepCoords({ coords, onChange, onSubmit, submitting, error }) {
  return (
    <div>
      <StepTitle emoji="🎯" title="Votre premier avis est prêt" hint="Indiquez-nous à qui il s'adresse." />
      <div className="space-y-3 mb-4">
        <TextField label="Prénom" value={coords.prenom} onChange={(v) => onChange({ prenom: v })} testid="sim-coords-prenom" autoComplete="given-name" />
        <TextField label="Nom" value={coords.nom} onChange={(v) => onChange({ nom: v })} testid="sim-coords-nom" autoComplete="family-name" />
        <div>
          <TextField label="Téléphone" value={coords.telephone} onChange={(v) => onChange({ telephone: v })} testid="sim-coords-telephone" type="tel" inputMode="tel" autoComplete="tel" placeholder="06 12 34 56 78 ou +32 …" />
          <p className="text-[11px] text-slate-400 mt-1 px-1 leading-snug" data-testid="sim-coords-phone-hint">{PHONE_HINT}</p>
        </div>
        <TextField label="Email" value={coords.email} onChange={(v) => onChange({ email: v })} testid="sim-coords-email" type="email" inputMode="email" autoComplete="email" />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 px-1">Ville de résidence actuelle <span className="text-slate-400">(facultatif)</span></div>
          <CityInput ville={coords.residence_ville} codePostal={coords.residence_code_postal} testid="sim-coords-residence"
            onChange={({ ville, code_postal }) => onChange({ residence_ville: ville, residence_code_postal: code_postal })} />
        </div>
      </div>
      <label className="flex items-start gap-3 mb-6 cursor-pointer" data-testid="sim-coords-consent">
        <input type="checkbox" checked={coords.consent || false} onChange={(e) => onChange({ consent: e.target.checked })} className="mt-1 w-5 h-5 accent-[color:var(--cf-primary)] flex-shrink-0" />
        <span className="text-[12px] text-slate-600 leading-relaxed" data-testid="sim-coords-consent-text">{CONSENT_TEXT}</span>
      </label>
      <p className="text-[11px] text-slate-400 mb-4 -mt-3" data-testid="sim-coords-legal-links">En savoir plus : <a href="/confidentialite" target="_blank" rel="noreferrer" className="underline">politique de confidentialité</a> · <a href="/mentions-legales" target="_blank" rel="noreferrer" className="underline">mentions légales</a></p>
      {error && <div className="mb-4 text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="sim-coords-error">{error}</div>}
      <BigButton onClick={onSubmit} disabled={submitting} testid="sim-coords-submit">{submitting ? "Analyse en cours…" : "Voir mon indice de faisabilité →"}</BigButton>
    </div>
  );
}

export function StepIndice({ result, prenom, config, onCallback, onReset }) {
  const level = result?.indicateur_faisabilite?.level || "orange";
  const ui = FAISABILITE_UI[level] || FAISABILITE_UI.orange;
  const points = result?.points_a_etudier || [];
  const courtier = config?.public_full_name || config?.display_name || "votre courtier";
  const prenomCourtier = (config?.display_name || courtier).split(" ")[0];
  return (
    <div data-testid="sim-result">
      <div className="mb-1 text-[13px] text-slate-500 font-semibold">{prenom || "Bonjour"},</div>
      <h1 className="font-extrabold text-slate-900 text-[24px] leading-tight tracking-tight mb-4">voici notre premier avis sur votre projet</h1>

      <div className="rounded-3xl p-5 border-2 sim-result-card" style={{ borderColor: ui.color, background: ui.bg }} data-testid="sim-result-faisabilite" data-level={level}>
        <div className="flex items-center gap-3">
          <SimIcon emoji={ui.emoji} size="lg" color={ui.color} />
          <div className="font-extrabold text-slate-900 text-[18px] leading-tight" data-testid="sim-result-faisabilite-label">{ui.title}</div>
        </div>
        <p className="text-[13.5px] text-slate-700 leading-relaxed mt-3">{ui.text}</p>
        <p className="text-[11px] text-slate-500 mt-3">Cet avis est indicatif et ne constitue ni un accord ni un refus de financement.</p>
      </div>

      {points.length > 0 && (
        <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-4" data-testid="sim-result-points">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-2">Ce que {prenomCourtier} va étudier avec vous</div>
          <ul className="space-y-2">
            {points.slice(0, 3).map((p, i) => (
              <li key={i} className="flex items-start gap-2 text-[14px] text-slate-800 font-semibold" data-testid={`sim-result-point-${i}`}><span className="text-[color:var(--cf-primary)]">•</span><span>{p}</span></li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-200 p-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-3">Comment ça se passe ensuite</div>
        {[`${prenomCourtier} vous rappelle`, "Vous échangez sur votre projet et la méthode", "Vous ouvrez votre espace et déposez vos documents"].map((t, i) => (
          <div key={i} className="flex items-center gap-3 py-1.5">
            <div className="w-7 h-7 rounded-full bg-[color:var(--cf-primary)] text-white text-[12px] font-extrabold flex items-center justify-center shrink-0">{i + 1}</div>
            <div className="text-[14px] text-slate-800 font-semibold">{t}</div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <BigButton onClick={onCallback} tone="dark" testid="sim-result-callback"><span className="inline-flex items-center justify-center gap-2">{isDAv2() ? <Phone size={18} /> : "📞"} Être rappelé par {courtier}</span></BigButton>
        <p className="text-[11.5px] text-slate-500 text-center mt-2">Sans engagement · vos coordonnées sont déjà renseignées</p>
      </div>
      <SimShareBlock slug={config?.slug} config={config} />
      <button type="button" onClick={onReset} className="mt-6 w-full text-slate-400 text-[12px] font-bold" data-testid="sim-result-reset">Refaire une simulation</button>
      <p className="mt-4 text-[11px] text-slate-400 text-center leading-relaxed">{config?.disclaimer}</p>
      <LegalLinks testid="sim-result-legal-links" />
    </div>
  );
}

export function StepCallbackConfirm({ coords, config, onConfirm, onBack, submitting, error }) {
  const courtier = config?.public_full_name || config?.display_name || "votre courtier";
  const [editPhone, setEditPhone] = useState(false);
  const [phone, setPhone] = useState(coords.telephone || "");
  const [creneau, setCreneau] = useState(null);
  return (
    <div data-testid="sim-callback-confirm">
      <StepTitle emoji="📞" title={`${courtier} vous rappelle`} />
      <div className="rounded-2xl bg-white border border-slate-200 p-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[14px] text-slate-800">Au <strong data-testid="sim-callback-phone">{phone || "—"}</strong></div>
          <button type="button" onClick={() => setEditPhone((v) => !v)} className="text-[12px] font-bold text-[color:var(--cf-primary)]" data-testid="sim-callback-edit-phone">Modifier</button>
        </div>
        {editPhone && (
          <>
            <input type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-xl border-2 border-slate-200 focus:border-[color:var(--cf-primary)] p-3 text-[16px] font-bold outline-none" data-testid="sim-callback-phone-input" />
            <p className="text-[11px] text-slate-400 leading-snug" data-testid="sim-callback-phone-hint">{PHONE_HINT}</p>
          </>
        )}
        <div className="text-[13px] text-slate-500">Email : {coords.email}</div>
      </div>
      <SubLabel>Créneau préféré <span className="text-slate-400 font-semibold">(facultatif)</span></SubLabel>
      <Chips options={CRENEAUX} value={creneau} onChange={setCreneau} testid="sim-callback-creneau" cols={2} />
      {error && <div className="mt-4 text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div>}
      <div className="mt-8 space-y-2">
        <BigButton onClick={() => onConfirm({ creneau, telephone: phone !== coords.telephone ? phone : null })} disabled={submitting || phone.replace(/\D/g, "").length < 8} testid="sim-callback-submit">
          {submitting ? "Envoi…" : "Confirmer ma demande de rappel"}
        </BigButton>
        <GhostButton onClick={onBack} testid="sim-callback-back">Retour</GhostButton>
      </div>
      <p className="text-[11px] text-slate-400 text-center mt-3">Aucun engagement. Vous pourrez poser toutes vos questions lors de l'appel.</p>
    </div>
  );
}

export function StepCallbackDone({ prenom, config, onStartQualif, creneauLabel }) {
  const prenomCourtier = (config?.display_name || config?.public_full_name || "Votre courtier").split(" ")[0];
  return (
    <div className="pt-4" data-testid="sim-callback-done">
      <div className="text-center mb-6">
        <div className="mb-4 flex justify-center"><SimIcon emoji="✅" size="xl" color="var(--cf-success)" /></div>
        <h1 className="font-extrabold text-slate-900 text-[24px] leading-tight tracking-tight">C'est noté{prenom ? `, ${prenom}` : ""}.</h1>
        <p className="text-slate-600 text-[15px] mt-3 leading-relaxed" data-testid="sim-callback-done-text"><strong>{prenomCourtier} vous recontacte rapidement.</strong>{creneauLabel ? ` Créneau souhaité : ${creneauLabel.toLowerCase()}.` : ""}</p>
      </div>
      {onStartQualif && config?.qualif_enabled === true && (
        <div className="rounded-2xl p-5 border-2 border-violet-200 bg-gradient-to-br from-violet-50 to-white sim-qualif-cta" data-testid="sim-qualif-cta-block">
          <div className="font-extrabold text-slate-900 text-[15px] leading-tight mb-1">Gagnez du temps avant l'appel</div>
          <p className="text-[12.5px] text-slate-600 leading-relaxed mb-4">Quelques questions pour que {prenomCourtier} ait déjà les éléments clés de votre projet. Facultatif.</p>
          <button type="button" onClick={onStartQualif} className="w-full min-h-[48px] rounded-2xl text-white font-extrabold text-[14px] active:scale-[0.98] transition shadow-md"
            style={{ background: "linear-gradient(135deg,var(--cf-accent),var(--cf-accent-hover))" }} data-testid="sim-result-qualif-start">Préparer mon échange (2 min)</button>
        </div>
      )}
      <SimShareBlock slug={config?.slug} config={config} />
    </div>
  );
}

export function StepQualifEnd({ config }) {
  const prenomCourtier = (config?.display_name || "Votre courtier").split(" ")[0];
  return (
    <div className="pt-8 text-center" data-testid="sim-qualif-end">
      <div className="mb-4 flex justify-center"><SimIcon emoji="🎯" size="xl" /></div>
      <h1 className="font-extrabold text-slate-900 text-[24px] leading-tight tracking-tight">Merci, {prenomCourtier} a tout ce qu'il lui faut.</h1>
      <p className="text-slate-600 text-[14px] mt-3 leading-relaxed">Vous n'aurez pas besoin de tout répéter lors de l'appel. À très vite.</p>
      <div className="text-left"><SimShareBlock slug={config?.slug} config={config} /></div>
    </div>
  );
}
